import { ReactComponent as ArchiproWordmark } from '@rocco/icons/svg/logo-text.svg';

import { cn } from '@rocco/utils/cn';
import { getImageAttrs } from '~/modules/images';
import { coverStatement } from './lookbook-intro';

interface LookbookCoverPreviewProps {
    title: string;
    professional: string;
    /** The project's introduction, which the cover's paragraph closes on. */
    intro?: string | null;
    /** The banner the cover's photograph is drawn from. */
    imageSrc?: string | null;
    className?: string;
}

/**
 * The lookbook's cover, drawn in the page rather than rendered from the PDF:
 * the same A4 page, pale ground, wordmark and imprint the document opens on.
 *
 * The photograph is in colour, as the printed cover's is, and on the page it
 * has a further job: standing off a frame that is itself greyscale.
 *
 * Every measure is the PDF's own point value as a share of the page's 595pt
 * width, in container units, so the cover keeps its proportions at whatever
 * width it is set.
 */
/**
 * The cover's photograph at the width it is drawn: 48% of a frame that is
 * itself around 960px, so the CDN is asked for that rendition rather than the
 * banner's original — which is 3200px wide and several megabytes for a
 * thumbnail the reader never sees at size.
 */
const COVER_WIDTH_PX = 480;

export const LookbookCoverPreview = ({
    title,
    professional,
    intro,
    imageSrc,
    className,
}: LookbookCoverPreviewProps) => {
    return (
        <div
            aria-hidden="true"
            className={cn(
                '@container relative aspect-[595/842] overflow-hidden bg-[#f1f0f3] text-black',
                className,
            )}
        >
            <ArchiproWordmark className="absolute top-[4.2cqw] left-[1.68cqw] h-auto w-[96.64cqw] [&_path]:fill-black" />
            <div className="absolute inset-x-[10cqw] top-[20.17cqw] flex flex-col items-center text-center font-sans font-bold uppercase">
                <p className="m-0 max-w-[50.42cqw] text-[1.613cqw] leading-[1.06] tracking-[-0.084cqw]">
                    {coverStatement(title, professional, intro)}
                </p>
                <p className="m-0 mt-[3.36cqw] text-[1.613cqw] leading-[0.87] tracking-[-0.084cqw]">
                    {professional}
                    <br />
                    {new Date().getFullYear()}
                </p>
            </div>
            {/* Sized outright: an image takes its height from its own
                proportions and ignores a bottom inset.

                In colour, against the desaturated frame it stands on: the
                cover is the one thing on that picture the reader is meant to
                reach for, and grey on grey it had nothing to say so. */}
            {imageSrc ? (
                <img
                    {...getImageAttrs(imageSrc, { width: COVER_WIDTH_PX })}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute top-[57.4%] left-[1.68cqw] h-[calc(42.6%-1.68cqw)] w-[96.64cqw] object-cover"
                />
            ) : null}
        </div>
    );
};
